import classNames from 'classnames';
import moment from 'moment';
import {
    always,
    assoc,
    assocPath,
    dissocPath,
    equals,
    isEmpty,
    keys,
    map,
    omit,
    pathOr,
    pipe,
    prop,
    values,
    when
} from 'ramda';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import DayPicker from 'react-day-picker';
import { connect, useSelector } from 'react-redux';
import { Link, RouteComponentProps, useLocation, useParams } from 'react-router-dom';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    useDndContext,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { ThemeProvider, Theme, StyledEngineProvider, Switch, Tooltip } from '@mui/material';
// import { toast } from 'react-toastify';
import { ReactComponent as AddIcon } from '../../../assets/svg/add.svg';
import { ReactComponent as ArrowBackSvg } from '../../../assets/svg/arrow_back.svg';
import { ReactComponent as CheckSvg } from '../../../assets/svg/check_24px.svg';
import { ReactComponent as CloseIcon } from '../../../assets/svg/close-icon.svg';
import { ReactComponent as EditSvg } from '../../../assets/svg/edit.svg';
import { ReactComponent as CalendarTodayIcon } from '../../../assets/svg/calendar_today.svg';
import { ReactComponent as PhotoCameraIcon } from '../../../assets/svg/photo-camera.svg';
import { BaseComponent } from '../../../shared/BaseComponent';
import { ACL, EXTRA_INFO_PRIORITY_FIELDS, UserCan } from '../../../shared/constants';
import * as labelsDb from '../../../shared/db/extra-info-db';
import * as devicedb from '../../../shared/db/devices-db';
import * as selectors from '../../../shared/db/tags-labels-selectors';
import { clientDb, clientStorage, keyGen } from '../../../shared/firebase';
import { makeAudit, targetVal } from '../../../shared/helpers';
import { iExtraInfo, iFenceDetails, iFullStoreState, iList, ItemType, UserAuth } from '../../../shared/interfaces';
import { localStorage } from '../../../shared/storage';
import { history, store } from '../../../stores/store';
import { Actions as GeneralActions } from '../../../stores/reducers/general-reducers';
import DashboardBlock from '../../DashboardBlock';
import Dialog, { DialogConfigSetter } from '../../Dialog';
import Modal from '../../Modal';
import ActionRow from '../../fence/tab-bodies/ActionRow/ActionRow';
import { FenceBodyLayout } from '../../fence/tab-bodies/BodyLayout/FenceBodyLayout/FenceBodyLayout';
import { Collapsible } from '../../general/Collapsible';
import MinimalTile from '../../general/MinimalTile';
import ModalActions from '../../menus/modal/modal-actions';
import { GoBack } from '../../menus/modal/modal-header';
import { DashboardSize } from '../../../stores/reducers/dashboardInfo';
import { muiSwitchTheme } from '../../../utils/muiThemes';
import './DayPicker.scss';
import FieldSuggestions from "./FieldSuggestions";
import { CALENDAR_INPUT_FORMAT, CustomNavBar, dateMask } from '../../CalendarInput/CalendarInput';
import MaskedInput from 'react-text-mask';
import SearchIcon from '../../SVG-components/search';
import GalleryModal from '../../elements/GalleryModal/GalleryModal';
import ClearModal from "../../ClearModal/ClearModal";
import { useFileUpload } from '../../../hooks/useFileUpload';
import NotepadWrapper from './NotepadWrapper';
import NewTagOrLabelModal from './NewTagOrLabelModal';
import { NoteFieldKind } from '../../../shared/db/note-extra-fields-db';

import styles from './label-item.module.scss';
import { compareByNameAscending } from '../../../utils/sorting';
import { Fa } from '../../elements/fa';
import { faHome } from '@fortawesome/free-solid-svg-icons';
import { faPencilAlt } from '@fortawesome/fontawesome-free-solid';



export const formatNumberText = (value?: string | number) => {
  const raw = typeof value === "number" ? value.toString() : value?.toString();
  if (!raw) return value;

  const trimmed = raw.trim();
  const dotGrouped = trimmed.replace(/,/g, "");

  if (/^[+-]?\d{1,3}(?:\.\d{3})+$/.test(dotGrouped)) {
    const cleaned = dotGrouped.replace(/\./g, "");
    return cleaned.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  if (!/^[+-]?\d+(?:\.\d+)?$/.test(dotGrouped)) return value;

  const hasSign = dotGrouped[0] === "-" || dotGrouped[0] === "+";
  const sign = hasSign ? dotGrouped[0] : "";
  const body = hasSign ? dotGrouped.slice(1) : dotGrouped;
  const [intPart, fracPart] = body.split(".");
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return fracPart !== undefined ? `${sign}${formattedInt}.${fracPart}` : `${sign}${formattedInt}`;
};

const stripThousandsCommas = (v: string) => (typeof v === 'string' ? v.replace(/,/g, '') : v);

const shouldFormatByKind = (kind?: string) => kind === 'number' || kind === 'money' || kind === 'legacy';

const formatTextValueForKind = (value: string, kind?: string) => {
    if (!shouldFormatByKind(kind)) return value;
    const formatted = formatNumberText(value);
    return formatted === undefined || formatted === null ? '' : String(formatted);
};

const sanitizeTextValueForKind = (value: string, kind?: string) => {
    if (!shouldFormatByKind(kind)) return value;

    const trimmedValue = stripThousandsCommas(value).trim();
    const sign = trimmedValue[0] === '-' || trimmedValue[0] === '+' ? trimmedValue[0] : '';
    let newValue = sign ? trimmedValue.slice(1) : trimmedValue;

    newValue = newValue.replace(/[^\d.]/g, '');

    const parts = newValue.split('.');
    if (parts.length > 2) newValue = parts[0] + '.' + parts.slice(1).join('');

    return `${sign}${newValue}`;
};

export interface iExtraInfoFile {
    url: string;
    format?: 'pdf' | 'image';
    fileName?: string;
}

export interface iExtraInfoItemValue {
    isPdf?: boolean;
    isDate?: boolean;
    fileName?: string;
    file?: { url: string; format?: 'pdf' | 'image'; fileName?: string };
    files?: iExtraInfoFile[];
    text?: string;
    boolean?: boolean;
    date?: string;
}

interface iExtraInfoItem {
    value: iExtraInfoItemValue | string | null;
    name: string;
    id: string;
    yesNoSelection?: boolean;
}

interface Category {
    id: number;
    name: string;
    isEditing: boolean;
}

interface ICategory {
    isCategory: boolean;
    [key: string]: string | iExtraInfoItemValue | boolean;
}

interface iState {
    toSave: iList<string | iExtraInfoItemValue | ICategory>;
    newOverlay?: boolean;
    newCategoryOverlay?: string | null;
    currentItemIdx?: string;
    currentCategory?: string | null;
    showDatePicker?: boolean;
    categories: Array<{ id: number, name: string, isEditing: boolean }>;
    editingId: number | null;
    editingValues?: { [key: string]: string };
    currentActiveIdx?: string | null;
    selectedDate: Date | null;
    displayMonth: Date | null;
    importModalOpen: boolean;
    clearModalOpen: boolean;
    copyModalOpen: boolean;
    rowOrder: string[];
    galleryOpen?: boolean;
    galleryIdx?: string | null;
    galleryCategory?: string | null;
    gallerySlideIndex?: number;
    editingLabelId: string | null;
}

type IProps = { itemType?: ItemType } & RouteComponentProps<{ itemId: string; isFence: string; type: ItemType }>;

type IPropsFromStore = {
    item: { 'extra-info': iList<string | iExtraInfoItemValue> };
    userCanDo?: Array<string>;
    fence: iFenceDetails | false;
    fenceId?: string | false;
    allLabels: iList<iExtraInfo>;
    authUser: UserAuth;
    size: DashboardSize;
    searchTerm: string;
    isSearchVis: boolean;
};

type IFullProps = IProps & IPropsFromStore & { dispatch: any };

const mapStateToProps = (state: iFullStoreState, ownProps): IPropsFromStore => ({
    item: selectors.getItemSelector(state, ownProps.match.params),
    fenceId: !!ownProps.match.params.isFence ? ownProps.match.params.itemId : false,
    fence: pathOr(false, ['general', 'fences', ownProps.itemType, ownProps.match.params.itemId, 'details'], state),
    userCanDo: state.auth.user!.acl?.can,
    allLabels: state.general.extraInfo,
    authUser: state.auth.user!,
    size: state.dashboardInfo.size,
    searchTerm: state.general.searchTerm,
    isSearchVis: state.general.showSearch,
});

interface iLabelSelectorOverlayProps {
    possible: iList<iExtraInfo>;
    onSelected: (id: string) => any;
    close: () => any;
    toSave: iList<string | iExtraInfoItemValue | ICategory>;
    currentCategory?: string | null;
}

const LabelSelectorOverlay = ({ possible, onSelected, close, toSave, currentCategory }: iLabelSelectorOverlayProps) => {
    const authUser = useSelector<iFullStoreState, UserAuth>((s) => s.auth.user);
    const isSuperAdmin = authUser.beToken['do-anything'];
    const [searchQuery, setSearchQuery] = useState('');

    const isLabelUsedInCurrentCategory = (labelId: string): boolean => {
        if (!currentCategory) return false;
        const categoryData = toSave[currentCategory];
        return typeof categoryData === 'object' &&
            'isCategory' in categoryData &&
            (categoryData as any).isCategory &&
            labelId in (categoryData as any);
    };

    return (
        <div className={classNames('modal-overlay', styles.overlayCss)} onClick={close}>
            <div className={styles.modal} onClick={event => event.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <span className={styles.modalTitle}>
                        {currentCategory ? `Add Label to ${currentCategory}` : 'Add Label'}
                    </span>
                    <div className={styles.buttonsSection}>
                        <Tooltip title={'Add New Label'} placement='top'>
                            <Link className={styles.addNewLabelButton} to="/extra-info"><AddIcon /></Link>
                        </Tooltip>
                        <Tooltip title={'Close Modal'} placement='top'>
                            <button onClick={close} className={styles.closeModalButton}>
                                <CloseIcon />
                            </button>
                        </Tooltip>
                    </div>
                </div>
                <div className={styles.labelsList}>
                    {pipe(
                        (x) => x,
                        map(prop('details')),
                        map((l: { id; name; isHidden; author }) => {
                            const userHasPermission = l.isHidden ? (isSuperAdmin || l.author === authUser.uid) : true;
                            const isSerialNumber = l.id === '0' && currentCategory !== null;
                            const isUsed = isLabelUsedInCurrentCategory(l.id);

                            if (!userHasPermission || isSerialNumber) return null;

                            return (
                                <button
                                    key={l.id}
                                    className={styles.labelButton}
                                    onClick={pipe(always(l.id), onSelected)}
                                    disabled={isUsed}
                                    style={isUsed ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                    title={isUsed ? 'This label is already used in this category' : ''}
                                >
                                    {l.name}
                                </button>
                            );
                        }) as any,
                        values,
                        when(isEmpty, always(<p>No more labels found</p>)) as any
                    )(possible)}
                </div>
            </div>
        </div>
    );
};

const DragAwareTooltip: React.FC<{ label: string; children: React.ReactElement }> = ({
    label,
    children
}) => {
    const { active } = useDndContext();
    const isAnyDragging = !!active;

    return isAnyDragging ? (
        children
    ) : (
        <Tooltip title={label} placement="top" arrow>
            {children}
        </Tooltip>
    );
};

interface SortableExtraInfoRowProps extends ExtraInfoRowProps {
    id: string;
    onEditLabel?: () => void;
}

const SortableExtraInfoRow: React.FC<SortableExtraInfoRowProps> = (props) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: props.id });

    const {
        isDragOver,
        handleDragOver,
        handleDragLeave,
        handleDrop,
    } = useFileUpload();

    const handleRowFileDrop = (e: React.DragEvent) => {
        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles.length > 0 && props.onFileDrop) {
            props.onFileDrop(droppedFiles[0]);
        }
        handleDrop(e);
    };

    const noScaleTransform = transform
        ? { ...transform, scaleX: 1, scaleY: 1 }
        : null;

    const style: React.CSSProperties = {
        transform: transform ? CSS.Transform.toString(noScaleTransform) : undefined,
        transition,
        opacity:  1,
        willChange: isDragging ? 'transform' : undefined,
        transformOrigin: '0 0',
    };

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className={classNames(styles.row, styles.draggableRow, {
                [styles.dragging]: isDragging,
                [styles.rowDragOver]: isDragOver,
            })}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleRowFileDrop}
        >
            <td className={classNames(styles.cell, styles.labelCell, styles.dragHandle)} {...attributes} {...listeners}>
                <div className={styles.labelWrapper}>
                <DragAwareTooltip label={props.label}>
                    <div className={styles.labelContent}>
                    <LabelCell label={props.label} />
                    </div>
                </DragAwareTooltip>
                {props.onEditLabel && (
                    <button
                    type="button"
                    className={styles.editLabelBtn}
                    onClick={(e) => { e.stopPropagation(); props.onEditLabel?.(); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    title="Edit label"
                    >
                        <Fa icon={faPencilAlt} />
                    </button>
                )}
                </div>
            </td>

            <ExtraInfoRow {...props} isDragOver={isDragOver} />
        </tr>
    );
};

interface DraggableTableProps {
    entries: iExtraInfoItem[];
    title?: string;
    allLabels: iList<iExtraInfo>;
    toSave: iList<string | iExtraInfoItemValue | ICategory>;
    currentActiveIdx: string | null | undefined;
    type: ItemType;
    allDeviceIds: string[];
    allPeopleIds: string[];
    onTextChange: (idx: string, value: string) => void;
    onToggle: (idx: string, value: boolean) => void;
    onDateChange: (idx: string, value: string) => void;
    onCalendarClick: (idx: string) => void;
    onClearDate: (idx: string) => void;
    onUploadClick: (idx: string) => void;
    onFileDrop: (idx: string, file: File) => void;
    onRemoveAll: (idx: string) => void;
    onOpenGallery: (idx: string, startAt?: number) => void;
    updateActiveIdx: (idx: string) => void;
    onDragEnd: (event: DragEndEvent) => void;
    onEditLabel?: (idx: string) => void;
}

const DraggableTable: React.FC<DraggableTableProps> = ({
    entries,
    title,
    allLabels,
    toSave,
    currentActiveIdx,
    type,
    allDeviceIds,
    allPeopleIds,
    onTextChange,
    onToggle,
    onDateChange,
    onCalendarClick,
    onClearDate,
    onUploadClick,
    onFileDrop,
    onRemoveAll,
    onOpenGallery,
    updateActiveIdx,
    onDragEnd,
    onEditLabel
}) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const isHiddenTable = title?.toLowerCase().includes('hidden');

    if (!entries.length) {
        return (
            <tr>
                <td colSpan={4} className={styles.noItemsTitle}>None Yet</td>
            </tr>
        );
    }

    return (
        <>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
            >
                <SortableContext items={entries.map(e => e.id)} strategy={verticalListSortingStrategy}>
                    <table className={styles.table}>
                        <tbody>
                            {title && (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className={classNames(styles.extraInfoRowsTitle, {
                                            [styles.hiddenTable]: isHiddenTable,
                                        })}
                                    >
                                        {title}
                                    </td>
                                </tr>)}

                            {entries.map((extraInfoItem) => {
                                const { value, id: idx } = extraInfoItem;

                                const files = value && typeof value === 'object' && Array.isArray(value.files)
                                    ? value.files
                                    : (value && typeof value === 'object' && value.file
                                        ? [value.file]
                                        : []);

                                const readOnly = (allLabels as any)[idx]?.details?.readOnly;

                                const textValueRaw =
                                    (value && typeof value === 'object' && 'text' in (value as any))
                                    ? (value as any).text
                                    : (typeof value === 'string' ? value : '');

                                const booleanValue = (value && typeof value === 'object' && 'boolean' in (value as any))
                                    ? (value as any).boolean
                                    : false;
                                const dateValue = value && typeof value === 'object' && 'date' in (value as any) && (value as any).date ? (value as any).date : '';
                                const dateRegex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-\d{4}$/;
                                const isDateValid = !dateValue || dateRegex.test(dateValue);

                                const kind = ((allLabels as any)[idx]?.details as any)?.kind || (extraInfoItem.yesNoSelection ? 'legacy-boolean' : 'legacy');
                                const textValue = typeof textValueRaw === 'string'
                                    ? formatTextValueForKind(textValueRaw, kind)
                                    : '';

                                const handleTextChange = (v: string) => onTextChange(idx, v);
                                const handleToggle = (v: boolean) => onToggle(idx, v);
                                const handleDateChange = (v: string) => onDateChange(idx, v);
                                const handleClearDate = () => onClearDate(idx);
                                const handleCalendarClick = () => onCalendarClick(idx);
                                const handleUploadClick = () => onUploadClick(idx);
                                const handleFileDrop = (file: File) => onFileDrop(idx, file);
                                const handleRemoveAll = () => onRemoveAll(idx);
                                const handleOpenGallery = (startAt?: number) => onOpenGallery(idx, startAt);

                                return (
                                    <SortableExtraInfoRow
                                        key={idx}
                                        id={idx}
                                        idx={idx}
                                        label={allLabels[idx]?.details?.name || idx}
                                        textValue={textValue}
                                        dateValue={typeof dateValue === 'string' ? dateValue : String(dateValue || '')}
                                        isDateValid={isDateValid}
                                        booleanValue={!!booleanValue}
                                        readOnly={readOnly}
                                        files={files}
                                        type={type}
                                        activeIdx={currentActiveIdx}
                                        updateActiveIdx={updateActiveIdx}
                                        allDeviceIds={allDeviceIds}
                                        allPeopleIds={allPeopleIds}
                                        onTextChange={handleTextChange}
                                        onToggle={handleToggle}
                                        onDateChange={handleDateChange}
                                        onCalendarClick={handleCalendarClick}
                                        onClearDate={handleClearDate}
                                        onUploadClick={handleUploadClick}
                                        onFileDrop={handleFileDrop}
                                        onRemoveAll={handleRemoveAll}
                                        onOpenGallery={handleOpenGallery}
                                        renderMode={kind}
                                        onEditLabel={onEditLabel ? () => onEditLabel(extraInfoItem.id) : undefined}
                                    />
                                );
                            })}
                        </tbody>
                    </table>
                </SortableContext>
            </DndContext>
        </>
    );
};

const DeviceNavigation = () => {
    const location = useLocation<any>();
    const params = useParams<{ itemId: string }>();

    const devices = useSelector<iFullStoreState, any>(
        (s) => s.devicesData.devicesDetails,
        (l, r) => equals(l, r)
    );

    const devicesArray = devices ? devices.valueSeq().toArray() : [];
    const devicesIdsArraySorted = devicesArray
            .sort(compareByNameAscending)
            .map(({ id }) => id);

    const currentDeviceId = params.itemId;

    const sortedDevices = location?.state?.sortedDevices ?? devicesIdsArraySorted;
    const currentIdx = sortedDevices.indexOf(currentDeviceId);

    if (currentIdx === -1) return null;

    const isFirst = currentIdx <= 0;
    const isLast = currentIdx >= sortedDevices.length - 1;

    const goTo = (index: number) => {
        if (index >= 0 && index < sortedDevices.length) {
            const nextId = sortedDevices[index];

            history.push({
                pathname: `/device/${nextId}/add/extra-info`,
                state: { sortedDevices: sortedDevices }
            });
        }
    };

    return (
        <div className={styles.navButtonsContainer}>
            <Tooltip title="Previous" placement="bottom">
                <button
                    onClick={() => goTo(currentIdx - 1)}
                    className={classNames(styles.navButton, { [styles.disabled]: isFirst })}
                    disabled={isFirst}
                >
                    <ArrowBackSvg />
                </button>
            </Tooltip>
            <Tooltip title="Home" placement="bottom">
                    <button
                        onClick={() => {
                            history.push({
                                pathname: `/device`,
                            });
                        }}
                        className={styles.deviceHomeButton}
                    >
                        <Fa icon={faHome} />
                    </button>
                </Tooltip>
            <Tooltip title="Next" placement="bottom">
                <button
                    onClick={() => goTo(currentIdx + 1)}
                    className={classNames(styles.navButton, { [styles.disabled]: isLast })}
                    disabled={isLast}
                >
                    <ArrowBackSvg style={{ transform: 'rotate(180deg)' }} />
                </button>
            </Tooltip>
        </div>
    );
};

const FileDropRow: React.FC<{ className?: string; onFileDrop?: (file: File) => void; children: React.ReactNode }> = ({ className, onFileDrop, children }) => {
    const {
        isDragOver,
        handleDragOver,
        handleDragLeave,
        handleDrop,
    } = useFileUpload();

    const handleRowDrop = (e: React.DragEvent) => {
        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles.length > 0 && onFileDrop) {
            onFileDrop(droppedFiles[0]);
        }
        handleDrop(e);
    };

    return (
        <tr
            className={classNames(className, { [styles.rowDragOver]: isDragOver })}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleRowDrop}
        >
            {children}
        </tr>
    );
};

class LabelItemComponent extends BaseComponent<IFullProps, iState> {
    uploadInput: React.RefObject<HTMLInputElement>;
    fileInput: React.RefObject<HTMLInputElement>;
    dialogRef: React.MutableRefObject<DialogConfigSetter | undefined>;
    saveTimeout: NodeJS.Timeout | null = null;

    constructor(props: IFullProps) {
        super(props);

        const initialCategories = (Object.entries(props.item['extra-info'] || {}) as [string, { isCategory?: boolean }][])
            .filter(([_, value]) => typeof value === 'object' && 'isCategory' in value && (value as any).isCategory)
            .map(([name], index) => ({
                id: index + 1,
                name,
                isEditing: false,
            }));

        this.state = {
            toSave: this.props.item['extra-info'] || {},
            categories: initialCategories,
            editingId: null,
            editingValues: {},
            newCategoryOverlay: null,
            currentCategory: null,
            currentActiveIdx: null,
            selectedDate: new Date(),
            displayMonth: new Date(),
            importModalOpen: false,
            clearModalOpen: false,
            copyModalOpen: false,
            galleryOpen: false,
            galleryIdx: null,
            galleryCategory: null,
            gallerySlideIndex: 0,
            editingLabelId: null,
            rowOrder: [],
        };

        this.uploadInput = React.createRef();
        this.fileInput = React.createRef();
        this.updateCurrentActiveIdx = this.updateCurrentActiveIdx.bind(this);
        this.dialogRef = React.createRef<DialogConfigSetter | undefined>();
        this.handleDragEnd = this.handleDragEnd.bind(this);
        this.handleFileDrop = this.handleFileDrop.bind(this);
    }

    debouncedSave = () => {
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }
        this.saveTimeout = setTimeout(() => {
            this._saveChanges();
        }, 500);
    };

    private getEditingContext() {
        const { authUser } = this.props;
        const { editingLabelId } = this.state;
        if (!authUser || !editingLabelId) return null;
        return { authUser, editingLabelId };
    }

    openLabelEditor = (labelId: string) => {
        this.setState({ editingLabelId: labelId });
    };

    closeLabelEditor = () => {
        this.setState({ editingLabelId: null });
    };

    handleUpdateLabel = async (values: { tagName: string; kind: NoteFieldKind; isLabelHidden: boolean; isYesNoType: boolean }) => {
        const ctx = this.getEditingContext();
        if (!ctx) return;

        const safeName = String(values.tagName || '').trim();
        const safeKind = labelsDb.normalizeLabelKind(values.kind);

        await labelsDb.updateLabelDetails(ctx.authUser, ctx.editingLabelId, safeName, safeKind);
        this.closeLabelEditor();
    };

    handleDeleteLabel = async () => {
        const ctx = this.getEditingContext();
        if (!ctx) return;

        const dialog = this.dialogRef.current;
        if (!dialog) return;

        const confirmed = await dialog({
            type: 'CONFIRM',
            title: 'Delete Label',
            body: 'Are you sure you want to delete this label? It will be removed from all devices.',
        });
        if (!confirmed) return;

        await labelsDb.deleteLabelFromDb(ctx.authUser, ctx.editingLabelId);
        this.closeLabelEditor();
    };

    handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;

        if (!over) return;

        if (active.id !== over.id) {
            this.setState((prevState) => {
                const currentEntries = this.getExtraInfoEntries();
                const currentIds = currentEntries.map(e => e.id);

                let sourceList = prevState.rowOrder;

                const activeInState = sourceList.includes(active.id as string);
                const overInState = sourceList.includes(over.id as string);

                if (!activeInState || !overInState) {
                    sourceList = currentIds;
                }

                const oldIndex = sourceList.indexOf(active.id as string);
                const newIndex = sourceList.indexOf(over?.id as string);

                if (oldIndex !== -1 && newIndex !== -1) {
                    const newRowOrder = arrayMove(sourceList, oldIndex, newIndex);

                    this.updateLabelOrder(newRowOrder);

                    return {
                        rowOrder: newRowOrder,
                    };
                }
                return null;
            });
        }
    }

    updateLabelOrder = async (newOrder: string[]) => {
        const { authUser } = this.props;

        try {
            const updates = {};

            if (newOrder && newOrder.length !== 0) {
                newOrder.forEach((labelId, index) => {
                    updates[`extra-info/${labelId}/details/order`] = index;
                });
            }

            if (Object.keys(updates).length > 0) {
                await clientDb().update(makeAudit(authUser, updates));
            }

        } catch (error) {
            console.error('Error updating label order:', error);
        }
    }

    openClearModal = () => {
        this.setState({ clearModalOpen: true });
    };

    closeClearModal = () => {
        this.setState({ clearModalOpen: false });
    };

    openCopyModal = () => {
        this.copyExtraInfoToClipboard();
    };

    formatExtraInfoValueForClipboard = (value: string | iExtraInfoItemValue | null | undefined): string => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'string') return value || '-';

        const parts: string[] = [];
        const textValueRaw = value.text;

        if (textValueRaw !== undefined && textValueRaw !== null && textValueRaw !== '') {
            const textValue = typeof textValueRaw === 'string' ? stripThousandsCommas(textValueRaw) : textValueRaw;
            const formatted = formatNumberText(textValue as any);
            parts.push(String(formatted ?? textValueRaw));
        }

        if (value.date) parts.push(String(value.date));

        const files = Array.isArray(value.files)
            ? value.files
            : value.file
                ? [{ ...value.file, format: value.file.format || (value.isPdf ? 'pdf' : 'image') }]
                : [];

        if (files.length) {
            const head = files[0]?.format === 'pdf' ? '[PDF]' : '[Image]';
            parts.push(files.length > 1 ? `${head}(+${files.length - 1})` : head);
        }

        return parts.join(', ') || '-';
    }

    copyExtraInfoToClipboard = async () => {
        const { allLabels } = this.props;
        const { toSave } = this.state;
        const { itemId } = this.props.match.params;

        const getLabelNameById = (labelId: string) =>
            allLabels?.[labelId]?.details?.name || labelId;

        const deviceName = store.getState().devicesData.devicesDetails.get(itemId)?.name;

        const lines: string[] = [];

        if (deviceName) {
            lines.push(`Vehicle: ${deviceName}`);
        }

        const rootEntries = this.getExtraInfoEntries();
        rootEntries.forEach((entry) => {
            lines.push(`${entry.name}: ${this.formatExtraInfoValueForClipboard(entry.value)}`);
        });

        Object.entries(toSave).forEach(([categoryName, categoryValue]) => {
            if (
                typeof categoryValue === 'object' &&
                categoryValue !== null &&
                'isCategory' in categoryValue &&
                (categoryValue as ICategory).isCategory
            ) {
                lines.push('');
                lines.push(`${categoryName}:`);

                Object.entries(categoryValue)
                    .filter(([key]) => key !== 'isCategory')
                    .forEach(([labelId, rawValue]) => {
                        lines.push(`- ${getLabelNameById(labelId)}: ${this.formatExtraInfoValueForClipboard(rawValue as string | iExtraInfoItemValue)}`);
                    });
            }
        });

        const textToCopy = lines.join('\n').trim();

        if (!textToCopy) return;

        try {
            await navigator.clipboard.writeText(textToCopy);
        } catch (err) {
            console.error('Failed to copy extra info to clipboard:', err);
        }
    }

    closeCopyModal = () => {
        this.setState({ copyModalOpen: false });
    };

    handleCopyData = async (targetItemId: string, targetName: string) => {
        const authUser = this.props.authUser;

        const gpsLabelEntry = this.getExtraInfoEntries().find(
            (label) =>
                label.name?.toLowerCase() === 'gps' ||
                label.name?.toLowerCase() === 'serial number'
        );

        let gpsValue = '';

        try {
            if (gpsLabelEntry?.id) {
                const snapshot = await clientDb()
                    .child(`devices/device-details/${targetItemId}/extra-info/${gpsLabelEntry.id}`)
                    .once('value');

                gpsValue = snapshot.val()?.text || '';
            }

            const { [gpsLabelEntry?.id || '']: _, ...dataWithoutGps } = this.state.toSave;

            const dataToSave = gpsLabelEntry?.id
                ? { ...dataWithoutGps, [gpsLabelEntry.id]: gpsValue }
                : { ...dataWithoutGps };

            await labelsDb.updateItemInfoValue(authUser)(
                ItemType.device,
                targetItemId,
                dataToSave as iList<string>,
            );

            // toast.success(`Copied data to ${targetName}`, {
            //     position: toast.POSITION.TOP_RIGHT,
            // });

        } catch (e) {
            console.error('Error copying data:', e);
            // toast.error('Error copying data', {
            //     position: toast.POSITION.TOP_RIGHT,
            // });
        }
    }

    setupDialog = (callBack: () => DialogConfigSetter) => {
        this.dialogRef.current = callBack();
    };

    _saveChanges = async () => {
        const { fenceId } = this.props;
        const { isFence, type, itemId } = this.props.match.params;

        const cleanedToSave = this.cleanFirebaseData(this.state.toSave);

        if (!!isFence) {
            await labelsDb.updateItemInfoValue(this.props.authUser)(type, `info/${fenceId}`, cleanedToSave as any);
        } else {
            await labelsDb.updateItemInfoValue(this.props.authUser)(type, itemId, cleanedToSave as any);
        }
    }

    saveItemAndGetLink = async (device, user, file: File) => {
        const { currentCategory, currentItemIdx } = this.state;
        const fileNameSafe = (file.name || 'file').replace(/[^\w.\-]/g, '_');
        const storagePath = currentCategory
            ? `devices/device-details/${device}/extra-info/${currentCategory}/${currentItemIdx}/${Date.now()}_${fileNameSafe}`
            : `devices/device-details/${device}/extra-info/${currentItemIdx}/${Date.now()}_${fileNameSafe}`;

        try {
            await clientStorage().child(storagePath).put(file);
            const url = await clientStorage().child(storagePath).getDownloadURL();
            return url;
        } catch (error) {
            console.error('Storage upload failed:', error);
            // toast.error('Failed to upload file due to permissions.', { position: toast.POSITION.TOP_RIGHT });
            throw error;
        }
    }

    cleanFirebaseData = (data) => {
        if (data === null || data === undefined) return null;
        if (typeof data !== 'object') return data;
        if (Array.isArray(data)) return data.map(item => this.cleanFirebaseData(item)).filter(item => item !== undefined);
        const cleaned = {};
        for (const [key, value] of Object.entries(data)) {
            if (value !== undefined) cleaned[key] = this.cleanFirebaseData(value);
        }
        return cleaned;
    };

    private isSupportedUpload(file: File): { ok: boolean; kind?: 'image' | 'pdf' } {
        const mime = (file.type || '').toLowerCase();
        const name = (file.name || '').toLowerCase();

        if (mime === 'application/pdf' || name.endsWith('.pdf')) return { ok: true, kind: 'pdf' as const };

        const isImgMime = mime.startsWith('image/');
        const isHeicLike = mime.includes('heic') || mime.includes('heif') || name.endsWith('.heic') || name.endsWith('.heif');
        if (isImgMime && !isHeicLike) return { ok: true, kind: 'image' as const };

        return { ok: false };
    }

    private processAndSaveFiles = async (files: File[], idx: string, categoryName?: string) => {
        const device = this.props.match.params.itemId;
        const user = store.getState().auth.user;

        try {
            const entries: iExtraInfoFile[] = [];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const check = this.isSupportedUpload(file);
                if (!check.ok) {
                    // toast.warn(`Skipped unsupported file: ${file.name}`, { position: toast.POSITION.TOP_RIGHT });
                    continue;
                }
                const url = await this.saveItemAndGetLink(device, user, file);
                if (check.kind === 'pdf') {
                    entries.push({ url, fileName: file.name || 'Document.pdf', format: 'pdf' });
                } else {
                    entries.push({ url, format: 'image' });
                }
            }

            if (!entries.length) return;

            const prevRaw = categoryName
                ? (this.state.toSave as any)[categoryName]?.[idx]
                : (this.state.toSave as any)[idx];

            const prev = (typeof prevRaw === 'object' && prevRaw !== null) ? { ...prevRaw } : {};
            const prevFiles = Array.isArray(prev.files)
                ? [...prev.files]
                : (prev.file ? [prev.file] : []);

            const nextFiles = [...prevFiles, ...entries];

            const updateData: any = { ...prev, files: nextFiles };
            delete updateData.file; delete updateData.isPdf; delete updateData.fileName;

            const path = categoryName
                ? `devices/device-details/${device}/extra-info/${categoryName}/${idx}`
                : `devices/device-details/${device}/extra-info/${idx}`;

            await clientDb().update(makeAudit(user, { [path]: updateData }));

            if (categoryName) {
                this.setState(assocPath(['toSave', categoryName, idx], updateData), this._saveChanges.bind(this));
            } else {
                this.setState(assocPath(['toSave', idx], updateData), this._saveChanges.bind(this));
            }

            if (files.length === 1) {
                // toast.success(`File "${files[0].name}" uploaded successfully`, { position: toast.POSITION.TOP_RIGHT });
            } else {
                // toast.success(`${files.length} files uploaded successfully`, { position: toast.POSITION.TOP_RIGHT });
            }

        } catch (error) {
            console.error('Upload failed:', error);
            // toast.error('Failed to upload files due to permissions.', { position: toast.POSITION.TOP_RIGHT });
        }
    };

    handleFileDrop = async (idx: string, file: File, categoryName?: string) => {
        this.setState({
            currentItemIdx: idx,
            currentCategory: categoryName || null
        });

        await this.processAndSaveFiles([file], idx, categoryName);
    };

    saveFiles = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
        const files: FileList | null = target.files;
        if (!files || files.length === 0) return;

        const { currentItemIdx, currentCategory } = this.state;

        if (!currentItemIdx) {
            console.error('No current item index set for file upload');
            return;
        }

        const filesArray = Array.from(files);
        await this.processAndSaveFiles(filesArray, currentItemIdx, currentCategory);

        target.value = '';
    };

    uploadClick = (idx: string, categoryName?: string) => {
        this.setState(prevState => ({
            ...prevState,
            currentItemIdx: idx,
            currentCategory: categoryName || null
        }));
        this.uploadInput.current!.click();
    }

    _newRow = () => this.setState(assoc('newOverlay', true))

    _newCategoryRow = (categoryName: string) => {
        const newItemId = `item_${Date.now()}`;
        this.setState({
            newCategoryOverlay: categoryName,
            currentItemIdx: newItemId,
        });
    }

    // handleAddCategory = (): void => {
    //     const newCategory = {
    //         id: Date.now(),
    //         name: "",
    //         isEditing: true,
    //     };

    //     this.setState({
    //         categories: [...this.state.categories, newCategory],
    //         editingId: newCategory.id,
    //         editingValues: {
    //             ...this.state.editingValues,
    //             [newCategory.id.toString()]: '',
    //         }
    //     });
    // };

    handleSaveCategory = (id: number): void => {
        const name = this.state.editingValues[id.toString()] || '';
        const trimmedName = name.trim();

        if (!trimmedName) return;

        this.setState(prevState => {
            const updatedEditingValues = { ...prevState.editingValues };
            delete updatedEditingValues[id.toString()];
            const oldCategory = prevState.categories.find(cat => cat.id === id);
            const updatedCategories = prevState.categories.map(cat =>
                cat.id === id ? { ...cat, name: trimmedName, isEditing: false } : cat
            );

            let updatedToSave = prevState.toSave;

            if ((updatedToSave as any)[trimmedName] && (!oldCategory || oldCategory.name !== trimmedName)) {
                // toast.error('Category with this name already exists!', { position: toast.POSITION.TOP_RIGHT });
                return prevState;
            }

            if (oldCategory && oldCategory.name && oldCategory.name !== trimmedName) {
                const oldCategoryData = (prevState.toSave as any)[oldCategory.name] || { isCategory: true };
                updatedToSave = {
                    ...(dissocPath([oldCategory.name], updatedToSave) as any),
                    [trimmedName]: oldCategoryData
                };
            } else if (!oldCategory?.name) {
                updatedToSave = assocPath([trimmedName], { isCategory: true }, updatedToSave);
            }

            return {
                ...prevState,
                categories: updatedCategories,
                editingId: null,
                editingValues: updatedEditingValues,
                toSave: updatedToSave
            };
        }, this._saveChanges.bind(this));
    };

    handleEditCategory = (id: number): void => {
        const categoryToEdit = this.state.categories.find(cat => cat.id === id);

        this.setState({
            categories: this.state.categories.map(cat =>
                cat.id === id ? { ...cat, isEditing: true } : cat
            ),
            editingId: id,
            editingValues: {
                ...this.state.editingValues,
                [id.toString()]: categoryToEdit?.name || '',
            }
        });
    };

    handleDeleteCategory = async (id: number, e: React.MouseEvent<HTMLButtonElement, MouseEvent>): Promise<void> => {
        e.stopPropagation();
        const category = this.state.categories.find(cat => cat.id === id);
        if (!category) return;

        const dialog = this.dialogRef.current;
        if (!dialog) return;

        const confirmed = await dialog({
            type: 'CONFIRM',
            title: 'Warning!',
            body: (
                <div>
                    <p>Deleting the category <b>{category.name}</b> will also delete all info items it contains.</p>
                    <p>Click OK to confirm.</p>
                </div>
            ),
        });

        if (!confirmed) return;

        this.setState(prevState => ({
            categories: prevState.categories.filter(cat => cat.id !== id),
            editingId: prevState.editingId === id ? null : prevState.editingId,
            editingValues: Object.fromEntries(
                Object.entries(prevState.editingValues).filter(([key]) => key !== id.toString())
            ),
            toSave: dissocPath([category.name], prevState.toSave)
        }), this._saveChanges.bind(this));
    };

    renderCategoryActions = (category: Category): React.ReactNode => {
        const isEditing = category.isEditing;
        const hasValidName = Boolean((this.state.editingValues[category.id.toString()] || "").trim());

        return (
            <div className={styles.categoryActions}>
                {isEditing ? (
                    <Tooltip title={hasValidName ? 'Save category' : 'Category name is required'} placement='top'>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                this.handleSaveCategory(category.id);
                            }}
                            disabled={!hasValidName}
                            className={classNames(styles.categoryActions__button, styles.categoryActions__buttons__saveButton)}
                        >
                            <CheckSvg />
                        </button>
                    </Tooltip>
                ) : (
                    <>
                        <Tooltip title='Edit category name' placement='top'>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this.handleEditCategory(category.id);
                                }}
                                className={styles.categoryActions__button}
                            >
                                <EditSvg />
                            </button>
                        </Tooltip>
                        <Tooltip title='Add info item to category' placement='top'>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this._newCategoryRow(category.name);
                                }}
                                className={styles.categoryActions__button}
                            >
                                <AddIcon />
                            </button>
                        </Tooltip>
                    </>
                )}
                <Tooltip title='Delete category' placement='top'>
                    <button
                        onClick={(e) => this.handleDeleteCategory(category.id, e)}
                        className={styles.categoryActions__button}
                    >
                        <CloseIcon />
                    </button>
                </Tooltip>
            </div>
        );
    };

    getFilesFor = (idx: string, categoryName?: string | null): iExtraInfoFile[] => {
        const v = categoryName
            ? (this.state.toSave as any)[categoryName]?.[idx]
            : (this.state.toSave as any)[idx];

        if (!v || typeof v !== 'object') return [];
        const files = Array.isArray((v as any).files)
            ? (v as any).files
            : (v as any).file
                ? [(v as any).file]
                : [];
        return files;
    };

    openGallery = (idx: string, categoryName?: string | null, startIndex: number = 0) => {
        const files = this.getFilesFor(idx, categoryName);
        if (!files.length) return;

        this.setState({
            galleryOpen: true,
            galleryIdx: idx,
            galleryCategory: categoryName || null,
            gallerySlideIndex: Math.min(startIndex, files.length - 1),
        });
    };

    closeGallery = () => this.setState({ galleryOpen: false, galleryIdx: null, galleryCategory: null, gallerySlideIndex: 0 });

    renderCategoryItems = (categoryName: string): React.ReactElement | null => {
        const categoryData = (this.state.toSave as any)[categoryName] as ICategory;

        if (!categoryData) {
            return (
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <tbody>
                            <tr>
                                <td colSpan={4} className={styles.noItemsTitle}>None Yet</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        }

        if (!categoryData.isCategory) return null;

        const state = store.getState();
        const allDeviceIds = Object.keys(state.devicesData.devicesDetails.toObject());
        const allPeopleIds = Object.keys(state.general.people);
        const { type } = this.props.match.params;

        const dateRegex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-\d{4}$/;

        const items = Object.entries(categoryData)
            .filter(([key, _]) => key !== 'isCategory')
            .map(([idx, value]) => {
                const readOnly = this.props.allLabels[idx]?.details?.readOnly;
                const valueType = (this.props.allLabels[idx]?.details as any)?.valueType;
                const kind = (this.props.allLabels[idx]?.details as any)?.kind || (this.props.allLabels[idx]?.details?.yesNoSelection ? 'legacy-boolean' : 'legacy');

                const textValueRaw =
                    typeof value === 'object' && value && 'text' in value
                        ? (value as any).text
                        : (typeof value === 'string' ? value : '');

                const dateValue = typeof value === 'object' && value && 'date' in value && (value as any).date ? (value as any).date : '';

                const files =
                    typeof value === 'object' && value
                        ? Array.isArray((value as any).files)
                            ? (value as any).files
                            : (value as any).file
                                ? [(value as any).file]
                                : []
                        : [];

                const isDateValid = !dateValue || dateRegex.test(dateValue);
                const booleanValue = (value && typeof value === 'object' && 'boolean' in (value as any)) ? (value as any).boolean : false;
                const textValue = typeof textValueRaw === 'string'
                    ? formatTextValueForKind(textValueRaw, kind)
                    : '';

                const onChangeHandler = (v: string) => {
                    const newValue = sanitizeTextValueForKind(v, kind);
                    const prevRaw = (this.state.toSave as any)[categoryName]?.[idx];
                    const prev = (typeof prevRaw === 'object' && prevRaw !== null) ? prevRaw : {};
                    const updateData = { ...prev, text: newValue };
                    this.setState(assocPath(['toSave', categoryName, idx], updateData), this.debouncedSave);
                };

                const onToggle = (v: boolean) => {
                    const prevRaw = (this.state.toSave as any)[categoryName]?.[idx];
                    const prev = (typeof prevRaw === 'object' && prevRaw !== null) ? prevRaw : {};
                    const updateData = { ...prev, boolean: v };
                    this.setState(assocPath(['toSave', categoryName, idx], updateData), this.debouncedSave);
                };

                const onDateChange = (dateStr: string) => {
                    if (dateStr && !dateRegex.test(dateStr)) {
                        this.setState(assocPath(['toSave', categoryName, idx, 'date'], dateStr));
                        return;
                    }
                    const prevRaw = (this.state.toSave as any)[categoryName]?.[idx];
                    const prev = (typeof prevRaw === 'object' && prevRaw !== null) ? prevRaw : {};
                    const updateData = { ...prev, date: dateStr };
                    const onlyDate = Object.keys(updateData).every(k => k === 'date' || (updateData as any)[k] === undefined);
                    this.setState(
                        assocPath(['toSave', categoryName, idx], onlyDate ? { date: dateStr } : updateData),
                        this.debouncedSave
                    );
                };

                const clearDate = () => {
                    const prevRaw = (this.state.toSave as any)[categoryName]?.[idx];
                    const prev = (typeof prevRaw === 'object' && prevRaw !== null) ? { ...prevRaw } : {};
                    if ('date' in prev) delete (prev as any).date;
                    this.setState(assocPath(['toSave', categoryName, idx], prev), this._saveChanges.bind(this));
                };

                return (
                    <FileDropRow key={idx} className={styles.row} onFileDrop={(file: File) => this.handleFileDrop(idx, file, categoryName)}>
                        <ExtraInfoRow
                            key={idx}
                            idx={idx}
                            label={this.props.allLabels[idx]?.details?.name || idx}
                            textValue={textValue as string}
                            dateValue={typeof dateValue === 'string' ? dateValue : (dateValue ? String(dateValue) : '')}
                            isDateValid={isDateValid}
                            readOnly={readOnly}
                            files={files}
                            type={type}
                            activeIdx={this.state.currentActiveIdx}
                            updateActiveIdx={this.updateCurrentActiveIdx}
                            allDeviceIds={allDeviceIds}
                            allPeopleIds={allPeopleIds}
                            onTextChange={onChangeHandler}
                            onToggle={onToggle}
                            booleanValue={!!booleanValue}
                            onDateChange={onDateChange}
                            onCalendarClick={() => this.datePickerClick(idx, categoryName)}
                            onClearDate={clearDate}
                            onUploadClick={() => this.uploadClick(idx, categoryName)}
                            onFileDrop={(file: File) => this.handleFileDrop(idx, file, categoryName)}
                            onRemoveAll={() => this.removeAllFilesAt(idx, categoryName)}
                            onOpenGallery={(startAt?: number) => this.openGallery(idx, categoryName, startAt ?? 0)}
                            renderMode={kind}
                            actionsCell={
                                <Tooltip title={'Remove item from category and delete data'} placement='top'>
                                    <button
                                        onClick={() => this.removeElement(idx, categoryName)}
                                        className={styles.row__head__actions__button}
                                        style={{ margin: 0, padding: 0 }}
                                    >
                                        <CloseIcon style={{ fill: '#d32f2f', height: '15px', width: '15px' }} />
                                    </button>
                                </Tooltip>
                            }
                        />
                    </FileDropRow>
                );
            });

        return (
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <tbody>
                        {(items.length > 0) ? items : (
                            <tr>
                                <td colSpan={4} className={styles.noItemsTitle}>None Yet</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    renderCategories = (): React.ReactNode => {
        const sortedCategories = [...this.state.categories].sort((a, b) => {
            if (a.name.toLowerCase() === 'vehicle') return -1;
            if (b.name.toLowerCase() === 'vehicle') return 1;
            return 0;
        });

        return (
            <>
                {sortedCategories.map(category => {
                    const categoryContent = this.renderCategoryItems(category.name);
                    return (
                        <Collapsible
                            key={category.id}
                            header={
                                <div className={category.isEditing ? styles.editingCategoryBlock : styles.readyCategoryBlock}>
                                    {category.isEditing ? (
                                        <input
                                            autoFocus
                                            value={this.state.editingValues[category.id.toString()] || ""}
                                            onChange={(e) => {
                                                this.setState({
                                                    editingValues: {
                                                        ...this.state.editingValues,
                                                        [category.id.toString()]: e.target.value || ""
                                                    }
                                                });
                                            }}
                                            placeholder='Category name'
                                            className={styles.editingCategoryBlock__input}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    ) : (
                                        <div className={styles.categoryName}>
                                            {category.name}
                                        </div>
                                    )}
                                    {this.renderCategoryActions(category)}
                                </div>
                            }
                            isCollapsed={false}
                            isCollapsible={false}
                            headerClassName={styles.collapsibleHeader}
                            bodyClassName={styles.collapsibleBody}
                        >
                            {categoryContent}
                        </Collapsible>
                    );
                })}
            </>
        );
    };

    _setLabel = (idx, value: string) => this.setState(assocPath(['toSave', idx], value))

    datePickerClick = (idx, categoryName?: string) => {
        const value = categoryName
            ? (this.state.toSave as any)[categoryName]?.[idx]
            : (this.state.toSave as any)[idx];
        const dateValue = value && typeof value === 'object' && (value as any).date ? (value as any).date : '';
        const parsedDate = dateValue ? moment(dateValue, 'MM-DD-YYYY').toDate() : new Date();

        this.setState(prevState => ({
            ...prevState,
            showDatePicker: !prevState.showDatePicker,
            currentItemIdx: idx,
            currentCategory: categoryName || null,
            selectedDate: parsedDate,
            displayMonth: parsedDate,
        }));
    }

    changeInputDate = (value: string) => {
        if (!value) {
            this.setState({ selectedDate: new Date(), displayMonth: new Date() });
            return;
        }
        const parsed = moment(value, CALENDAR_INPUT_FORMAT, true);
        if (parsed.isValid()) {
            const date = parsed.toDate();
            this.setState({ selectedDate: date, displayMonth: date });
        }
    };

    handleDateSelect = async (day) => {
        const device = this.props.match.params.itemId;
        const user = store.getState().auth.user;
        const date = moment(day).format('MM-DD-YYYY');
        const { currentItemIdx, currentCategory } = this.state;

        const currentData = currentCategory
            ? this.state.toSave[currentCategory]?.[currentItemIdx] || {}
            : this.state.toSave[currentItemIdx] || {};

        const updateData = { ...currentData, date };

        if (currentCategory) {
            await clientDb().update(makeAudit(user, {
                [`devices/device-details/${device}/extra-info/${currentCategory}/${currentItemIdx}`]: updateData
            }));
            this.setState(assocPath(['toSave', currentCategory, currentItemIdx], updateData), this._saveChanges.bind(this));
        } else {
            await clientDb().update(makeAudit(user, {
                [`devices/device-details/${device}/extra-info/${currentItemIdx}`]: updateData
            }));
            this.setState(assocPath(['toSave', currentItemIdx], updateData), this._saveChanges.bind(this));
        }

        this.setState(prevState => ({
            ...prevState,
            showDatePicker: false
        }));
    };

    removeElement = async (idx: string, categoryName?: string) => {
        const device = this.props.match.params.itemId;
        const user = store.getState().auth.user;

        if (categoryName) {
            this.setState(dissocPath(['toSave', categoryName, idx]));
            await clientDb().update(makeAudit(user, {
                [`devices/device-details/${device}/extra-info/${categoryName}/${idx}`]: null
            }));
        } else {
            this.setState(dissocPath(['toSave', idx]));
            await clientDb().update(makeAudit(user, {
                [`devices/device-details/${device}/extra-info/${idx}`]: null
            }));
        }

        this._saveChanges();
    }

    removeFileAt = async (idx: string, categoryName?: string, fileIndex: number = 0) => {
        const device = this.props.match.params.itemId;
        const user = store.getState().auth.user;

        const prevRaw = categoryName
            ? (this.state.toSave as any)[categoryName]?.[idx]
            : (this.state.toSave as any)[idx];

        const prev = (typeof prevRaw === 'object' && prevRaw !== null) ? { ...prevRaw } : {};
        const prevFiles = Array.isArray((prev as any).files)
            ? [...(prev as any).files]
            : ((prev as any).file ? [(prev as any).file] : []);

        if (!prevFiles.length) {
            delete (prev as any).file;
            const path = categoryName
                ? `devices/device-details/${device}/extra-info/${categoryName}/${idx}`
                : `devices/device-details/${device}/extra-info/${idx}`;
            await clientDb().update(makeAudit(user, { [path]: prev }));
            if (categoryName) this.setState(assocPath(['toSave', categoryName, idx], prev), this._saveChanges.bind(this));
            else this.setState(assocPath(['toSave', idx], prev), this._saveChanges.bind(this));
            return;
        }

        if (fileIndex < 0 || fileIndex >= prevFiles.length) fileIndex = 0;

        prevFiles.splice(fileIndex, 1);

        const updateData: any = { ...prev };
        if (prevFiles.length > 0) updateData.files = prevFiles;
        else delete updateData.files;
        delete updateData.file; delete updateData.isPdf; delete updateData.fileName;

        const path = categoryName
            ? `devices/device-details/${device}/extra-info/${categoryName}/${idx}`
            : `devices/device-details/${device}/extra-info/${idx}`;

        await clientDb().update(makeAudit(user, { [path]: updateData }));

        if (categoryName) {
            this.setState(assocPath(['toSave', categoryName, idx], updateData), () => {

                if (this.state.galleryOpen && this.state.galleryIdx === idx && this.state.galleryCategory === categoryName) {
                    const newLen = (updateData.files || []).length;
                    if (newLen === 0) this.closeGallery();
                    else this.setState({ gallerySlideIndex: Math.min(this.state.gallerySlideIndex!, newLen - 1) });
                }
                this._saveChanges();
            });
        } else {
            this.setState(assocPath(['toSave', idx], updateData), () => {
                if (this.state.galleryOpen && this.state.galleryIdx === idx && !this.state.galleryCategory) {
                    const newLen = (updateData.files || []).length;
                    if (newLen === 0) this.closeGallery();
                    else this.setState({ gallerySlideIndex: Math.min(this.state.gallerySlideIndex!, newLen - 1) });
                }
                this._saveChanges();
            });
        }
    };

    removeAllFilesAt = async (idx: string, categoryName?: string) => {
        const device = this.props.match.params.itemId;
        const user = store.getState().auth.user;

        const prevRaw = categoryName
            ? (this.state.toSave as any)[categoryName]?.[idx]
            : (this.state.toSave as any)[idx];

        const prev = (typeof prevRaw === 'object' && prevRaw !== null) ? { ...prevRaw } : {};

        delete (prev as any).files;
        delete (prev as any).file;
        delete (prev as any).isPdf;
        delete (prev as any).fileName;

        const path = categoryName
            ? `devices/device-details/${device}/extra-info/${categoryName}/${idx}`
            : `devices/device-details/${device}/extra-info/${idx}`;

        await clientDb().update(makeAudit(user, { [path]: prev }));

        const setStatePath = categoryName ? ['toSave', categoryName, idx] : ['toSave', idx];
        this.setState(assocPath(setStatePath as any, prev), () => {
            if (this.state.galleryOpen && this.state.galleryIdx === idx && (this.state.galleryCategory || null) === (categoryName || null)) {
                this.closeGallery();
            }
            this._saveChanges();
        });
    };

    initializeRowOrder = () => {
        const extraInfoEntries = this.getExtraInfoEntries();
        const rowOrder = extraInfoEntries.map(entry => entry.id);
        this.setState({ rowOrder });
    }

    getExtraInfoEntries = (): iExtraInfoItem[] => {
        const { allLabels } = this.props;
        const { toSave, rowOrder } = this.state;

        const isLabelInCategories = (labelId: string): boolean => {
            for (const [categoryName, categoryData] of Object.entries(toSave)) {
                if (
                    typeof categoryData === 'object' &&
                    'isCategory' in (categoryData as any) &&
                    (categoryData as any).isCategory &&
                    labelId in (categoryData as any)
                ) return true;
            }
            return false;
        };

        const entries = Object.values(allLabels)
            .filter(label => {
                const labelId = label.details['id'];
                return !isLabelInCategories(labelId);
            })
            .map(label => {
                const rawValue = (toSave as any)[label.details['id']] || null;
                const isICategory = typeof rawValue === 'object' && rawValue !== null && 'isCategory' in (rawValue as any);

                return {
                    value: isICategory ? null : (rawValue as string | iExtraInfoItemValue),
                    name: label.details.name,
                    id: label.details['id'],
                    yesNoSelection: (label.details as any)?.yesNoSelection
                };
            });

        const sortedEntries = [...entries].sort((a, b) => {
            const orderA = allLabels[a.id]?.details?.order;
            const orderB = allLabels[b.id]?.details?.order;
            if (orderA !== undefined && orderB !== undefined && orderA !== orderB) {
                return orderA - orderB;
            }

            const aIndex = rowOrder.indexOf(a.id);
            const bIndex = rowOrder.indexOf(b.id);
            if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
            }
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;

            const priorityMap = new Map(EXTRA_INFO_PRIORITY_FIELDS.map((name, idx) => [name, idx]));
            const aIdx = priorityMap.has(a.name) ? priorityMap.get(a.name) : EXTRA_INFO_PRIORITY_FIELDS.length + 1;
            const bIdx = priorityMap.has(b.name) ? priorityMap.get(b.name) : EXTRA_INFO_PRIORITY_FIELDS.length + 1;
            if (aIdx !== bIdx) return aIdx - bIdx;

            return a.name.localeCompare(b.name);
        });

        return sortedEntries;
    };

    updateCurrentActiveIdx(idx) {
        this.setState((prevState) => ({ ...prevState, currentActiveIdx: idx }))
    }

    exportEmptyExtraInfoCsv = () => {
        const { allLabels } = this.props;

        const headers = ['Label', 'Value', 'Date'];
        const rows: string[][] = [headers];

        const priorityMap = new Map(EXTRA_INFO_PRIORITY_FIELDS.map((name, idx) => [name, idx]));
        const sortedLabels = Object.entries(allLabels)
            .map(([id, label]) => ({ id, name: label.details.name }))
            .sort((a, b) => {
                const aIdx = priorityMap.has(a.name) ? priorityMap.get(a.name) : EXTRA_INFO_PRIORITY_FIELDS.length + 1;
                const bIdx = priorityMap.has(b.name) ? priorityMap.get(b.name) : EXTRA_INFO_PRIORITY_FIELDS.length + 1;
                if (aIdx !== bIdx) return aIdx - bIdx;
                return a.name.localeCompare(b.name);
            });

        const labelRows = sortedLabels.map(label => [label.name, '', '']);

        rows.push(...labelRows);

        const processRow = (row: string[]) =>
            row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');

        const csvData = rows.map(processRow).join('\n');
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });

        const filename = 'ExtraInfoTemplate.csv';
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    triggerFileInput = () => this.fileInput.current!.click();

    handleImportedExtraInfoFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const dateRegex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])\-\d{4}$/;
        const { toSave } = this.state;
        const { allLabels } = this.props;
        const fileInput = this.fileInput.current;

        const onSuccess = () => {
            if (fileInput) fileInput.value = '';
            // toast.success('Data imported successfully!', { position: toast.POSITION.TOP_RIGHT });
        };
        const onError = (msg: string, error?: any) => {
            console.error(msg, error);
            // toast.error(msg, { position: toast.POSITION.TOP_RIGHT });
            if (fileInput) fileInput.value = '';
        };

        try {
            if (file.name.endsWith('.csv')) {
                await this.parseCsvFile(file, dateRegex, allLabels, toSave, onSuccess, onError);
            } else if (file.name.endsWith('.xlsx')) {
                await this.parseXlsxFile(file, dateRegex, allLabels, toSave, onSuccess, onError);
            } else {
                onError('Unsupported file format. Please upload a CSV or XLSX file.');
            }
        } catch (error) {
            onError('Failed to parse the uploaded file.', error);
        }
    };

    parseCsvFile = async (
        file: File,
        dateRegex: RegExp,
        allLabels: iList<iExtraInfo>,
        toSave: iList<string | iExtraInfoItemValue | ICategory>,
        onSuccess: () => void,
        onError: (msg: string, error?: any) => void
    ) => {
        await new Promise<void>((resolve, reject) => {
            Papa.parse(file, {
                complete: (result) => {
                    try {
                        const rows = result.data as any[];
                        this.processImportedData(rows, dateRegex, allLabels, toSave);
                        onSuccess();
                        resolve();
                    } catch (e) {
                        onError('Failed to process CSV data.', e);
                        reject(e);
                    }
                },
                header: false,
                skipEmptyLines: true,
                error: (error) => {
                    onError('CSV parsing error.', error);
                    reject(error);
                }
            });
        });
    };

    parseXlsxFile = async (
        file: File,
        dateRegex: RegExp,
        allLabels: iList<iExtraInfo>,
        toSave: iList<string | iExtraInfoItemValue | ICategory>,
        onSuccess: () => void,
        onError: (msg: string, error?: any) => void
    ) => {
        const reader = new FileReader();
        await new Promise<void>((resolve, reject) => {
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target!.result as ArrayBuffer);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false }) as any[];
                    this.processImportedData(rows, dateRegex, allLabels, toSave);
                    onSuccess();
                    resolve();
                } catch (err) {
                    onError('Failed to process XLSX data.', err);
                    reject(err);
                }
            };
            reader.onerror = () => {
                onError('Failed to read XLSX file.');
                reject(new Error('Failed to read XLSX file'));
            };
            reader.readAsArrayBuffer(file);
        });
    };

    processImportedData = (
        rows: any[],
        dateRegex: RegExp,
        allLabels: iList<iExtraInfo>,
        toSave: iList<string | iExtraInfoItemValue | ICategory>
    ) => {
        let updatedToSave = { ...toSave };

        const isICategory = (data: string | iExtraInfoItemValue | ICategory): data is ICategory => {
            return typeof data === 'object' && data !== null && 'isCategory' in data;
        };

        const normalizeDate = (date: string): string => {
            if (!date || date === '-' || date === '') return '';
            if (/^00-00-\d{4}$/.test(date)) return '';
            const parsedDate = moment(date, ['M-D-YYYY', 'MM-DD-YYYY', 'M/D/YYYY', 'MM/DD/YYYY'], true);
            return parsedDate.isValid() ? parsedDate.format('MM-DD-YYYY') : '';
        };

        rows.forEach(row => {
            const [label, value, date] = row;
            if (!label) return;
            if (['serial number', 'gps'].includes(String(label).toLowerCase())) return;

            const labelEntry = Object.values(allLabels).find(l => l.details.name === label);
            if (!labelEntry) return;
            const labelId = labelEntry.details.id;
            const isYesNoSelection = (labelEntry.details as any).yesNoSelection;

            let parsedValue = value === '-' ? '' : value;
            if (isYesNoSelection && typeof parsedValue === 'string' && parsedValue.includes('boolean')) {
                try {
                    const parsed = JSON.parse(parsedValue);
                    parsedValue = (parsed as any).boolean !== undefined ? (parsed as any).boolean : parsedValue;
                } catch { /* ignore */ }
            }

            const normalizedDate = normalizeDate(date);
            const isValidDate = normalizedDate && dateRegex.test(normalizedDate);

            const existingData = (updatedToSave as any)[labelId] || {};
            const existing: iExtraInfoItemValue = isICategory(existingData)
                ? {}
                : typeof existingData === 'string'
                    ? { text: existingData }
                    : existingData;

            const newData: iExtraInfoItemValue = {
                ...existing,
                ...(isYesNoSelection && parsedValue !== undefined
                    ? { boolean: !!parsedValue }
                    : parsedValue
                        ? { text: parsedValue }
                        : {}),
                ...(isValidDate ? { date: normalizedDate } : {}),
            };

            if ((newData as any).text || (newData as any).date || ((newData as any).files && (newData as any).files.length > 0) || (newData as any).boolean !== undefined) {
                updatedToSave = assocPath([labelId], newData, updatedToSave);
            }
        });

        this.setState({ toSave: updatedToSave, importModalOpen: false }, this._saveChanges.bind(this));
    };

    handleShowSearch = () => {
        if (!this.props.isSearchVis) {
            this.props.dispatch(GeneralActions.SHOW_SEARCH);
        }
    };

    clearTable = async (): Promise<void> => {
        const { type, itemId } = this.props.match.params;

        const newLabels: iList<iExtraInfo> = {};

        if (this.props.allLabels["0"]) {
            newLabels["0"] = this.props.allLabels["0"];
        }

        await labelsDb.updateItemInfoValue(this.props.authUser)(type, itemId, newLabels as any);

        this.setState({ toSave: newLabels as iList<string | iExtraInfoItemValue | ICategory> });

        this.setState({ clearModalOpen: false });
    };

    debugRender = () => {
        const { fence, allLabels, userCanDo, item, authUser, match } = this.props;
        const { type, itemId, isFence } = this.props.match.params;
        const { newOverlay = false, toSave, newCategoryOverlay } = this.state;
        const isSubAdmin = localStorage.get('login-init-be-token')[UserCan.SUB_ADMIN];
        const subUsers = isSubAdmin ? localStorage.get('login-init-user')['subUsers'] : [];
        const itemName: string = type === ItemType.device ? item['name'] : type === ItemType.person ? item['displayName'] : '';
        const extraInfoEntries: iExtraInfoItem[] = this.getExtraInfoEntries();
        const state = store.getState();
        const allDeviceIds = Object.keys(state.devicesData.devicesDetails.toObject());
        const allPeopleIds = Object.keys(state.general.people);
        const isSuperUser = ACL.check(UserCan.DO_ANYTHING, userCanDo);

        const DynamicLayout: any = !!isFence ? FenceBodyLayout : DashboardBlock;
        const DynamicHeader = !!isFence ? <div><ActionRow title={(fence || {} as any).name} canBack actions={[]} /></div> : (
            <div className={styles.extraInfoHeader}>
                <GoBack>
                    <ArrowBackSvg style={{ position: 'absolute', top: 0, left: 0, color: '#FF7C02' }} />
                </GoBack>
                <h3 style={{ fontSize: '14px', flex: 1, wordBreak: 'break-all', marginBottom: 0 }}>{itemName.length > 0 ? itemName : ''}</h3>
                {!isFence && type === ItemType.device && <DeviceNavigation />}
                {type === ItemType.device && (<div className={styles.searchContainer}>
                    <SearchIcon className={styles.searchIcon} />
                    <input
                        className={styles.searchInput}
                        type="text"
                        id="search-input"
                        onFocus={this.handleShowSearch}
                        defaultValue={this.props.searchTerm}
                        placeholder='Search'
                    />
                </div>)}

            </div>
        )

        const needsSave = !equals(this.state.toSave, item['extra-info'] || {});

        const handleTextChange = (idx: string, value: string) => {
            const kind = ((allLabels as any)[idx]?.details as any)?.kind || 'legacy';
            const newValue = sanitizeTextValueForKind(value, kind);
            const prevRaw = this.state.toSave[idx];
            const prev = typeof prevRaw === 'object' && prevRaw !== null ? prevRaw : {};
            const updateData = { ...prev, text: newValue };
            this.setState(assocPath(['toSave', idx], updateData), this.debouncedSave);
        };

        const handleToggle = (idx: string, value: boolean) => {
            const labelName = allLabels[idx]?.details?.name;

            if (labelName === "Sold | Scrapped") {
                if (type === ItemType.device && itemId) {
                    clientDb().update(makeAudit(authUser, {
                        [`devices/device-details/${itemId}/isArchived`]: value
                    })).then(() => {
                        const msg = value ? 'Device archived successfully' : 'Device restored (unarchived) successfully';
                        // toast.success(msg, { position: toast.POSITION.TOP_RIGHT });
                    }).catch((e) => {
                        console.error("Error updating archive status:", e);
                        // toast.error('Error updating archive status', { position: toast.POSITION.TOP_RIGHT });
                    });
                }
            }

            const prevRaw = this.state.toSave[idx];
            const prev = typeof prevRaw === 'object' && prevRaw !== null ? prevRaw : {};
            const updateData = { ...prev, boolean: value };

            const isSoldLabel = labelName === 'Sold';

            if (isSoldLabel && type === ItemType.device && itemId) {
                devicedb.updateDeviceDetails(this.props.authUser)(itemId, {
                    isArchived: value
                }).catch((error) => {
                    console.error('Failed to update isArchived:', error);
                    // toast.error('Failed to update archive status', { position: toast.POSITION.TOP_RIGHT });
                });
            }

            this.setState(assocPath(['toSave', idx], updateData), this._saveChanges.bind(this));
        };

        const handleDateChange = (idx: string, dateStr: string) => {
            const dateRegex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-\d{4}$/;
            if (dateStr && !dateRegex.test(dateStr)) {
                this.setState(assocPath(['toSave', idx, 'date'], dateStr));
                return;
            }
            const prevRaw = this.state.toSave[idx];
            const prev = typeof prevRaw === 'object' && prevRaw !== null ? prevRaw : {};
            const updateData = { ...prev, date: dateStr };
            const onlyDate = Object.keys(updateData).every(k => k === 'date' || updateData[k] === undefined);
            this.setState(
                assocPath(['toSave', idx], onlyDate ? { date: dateStr } : updateData),
                this.debouncedSave
            );
        };

        const handleClearDate = (idx: string) => {
            const prevRaw = this.state.toSave[idx];
            const prev = typeof prevRaw === 'object' && prevRaw !== null ? prevRaw : {};
            if ('date' in prev) delete prev.date;
            this.setState(assocPath(['toSave', idx], prev), this._saveChanges.bind(this));
        };

        const handleCalendarClick = (idx: string) => this.datePickerClick(idx);
        const handleUploadClick = (idx: string) => this.uploadClick(idx);
        const handleFileDrop = (idx: string, file: File) => this.handleFileDrop(idx, file);
        const handleRemoveAll = (idx: string) => this.removeAllFilesAt(idx);
        const handleOpenGallery = (idx: string, startAt?: number) => this.openGallery(idx, undefined, startAt ?? 0);

        const hiddenLabels = extraInfoEntries.filter(item => allLabels[item.id]?.details?.isHidden);
        const visibleLabels = extraInfoEntries.filter(item => !allLabels[item.id]?.details?.isHidden);

        const isTbs = extraInfoEntries.find((entry) => {
            return (
                !this.props.isSearchVis &&
                entry.yesNoSelection &&
                (entry.value as iExtraInfoItemValue)?.boolean &&
                entry.name === 'TBS'
            );
        });

        return (
            <>
                <DynamicLayout
                    mapOnly={!itemId || !type}
                    modalActions={
                        !needsSave ? false : (
                            <ModalActions
                                isFence
                                hidden={!needsSave}
                                buttons={[
                                    { click: history.goBack, title: 'Cancel' },
                                    { click: this._saveChanges, title: 'Update', primary: true },
                                ]}
                            />
                        )
                    }
                    className={classNames({ [styles.tbsBackground]: isTbs })}
                >
                    {DynamicHeader}
                    <div style={!!isFence ? { padding: 8 } : {}} className={styles.labelItemContainer}>
                        {!isFence ? null : (<MinimalTile>Change Labels</MinimalTile>)}
                        <div className={styles.container}>
                            <div className={styles.tableContainer}>
                                {this.renderCategories()}
                            </div>
                            <div className={styles.extraTableContainer}>
                                <DraggableTable
                                    key={itemId}
                                    entries={visibleLabels}
                                    allLabels={allLabels}
                                    toSave={toSave}
                                    currentActiveIdx={this.state.currentActiveIdx}
                                    type={type}
                                    allDeviceIds={allDeviceIds}
                                    allPeopleIds={allPeopleIds}
                                    onTextChange={handleTextChange}
                                    onToggle={handleToggle}
                                    onDateChange={handleDateChange}
                                    onCalendarClick={handleCalendarClick}
                                    onClearDate={handleClearDate}
                                    onUploadClick={handleUploadClick}
                                    onFileDrop={handleFileDrop}
                                    onRemoveAll={handleRemoveAll}
                                    onOpenGallery={handleOpenGallery}
                                    updateActiveIdx={this.updateCurrentActiveIdx}
                                    onDragEnd={this.handleDragEnd}
                                    onEditLabel={this.openLabelEditor}
                                />
                                {isSuperUser && <DraggableTable
                                    entries={hiddenLabels}
                                    key={`hidden-${itemId}`}
                                    title="Hidden"
                                    allLabels={allLabels}
                                    toSave={toSave}
                                    currentActiveIdx={this.state.currentActiveIdx}
                                    type={type}
                                    allDeviceIds={allDeviceIds}
                                    allPeopleIds={allPeopleIds}
                                    onTextChange={handleTextChange}
                                    onToggle={handleToggle}
                                    onDateChange={handleDateChange}
                                    onCalendarClick={handleCalendarClick}
                                    onClearDate={handleClearDate}
                                    onUploadClick={handleUploadClick}
                                    onFileDrop={handleFileDrop}
                                    onRemoveAll={handleRemoveAll}
                                    onOpenGallery={handleOpenGallery}
                                    updateActiveIdx={this.updateCurrentActiveIdx}
                                    onDragEnd={this.handleDragEnd}
                                    onEditLabel={this.openLabelEditor}
                                />}

                                <div style={{ marginTop: '10px', width: '100%' }}>
                                    <NotepadWrapper
                                        itemId={itemId}
                                        itemType={type}
                                        isFence={!!isFence}
                                        ownerTitle={itemName || itemId}
                                    />
                                </div>
                            </div>
                            <input
                                name="upload"
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/gif,application/pdf"
                                multiple
                                onChange={this.saveFiles}
                                style={{ display: 'none' }}
                                ref={this.uploadInput}
                            />
                            <input
                                name="import"
                                type="file"
                                accept=".csv,.xlsx"
                                onChange={this.handleImportedExtraInfoFileUpload}
                                style={{ display: 'none' }}
                                ref={this.fileInput}
                            />
                        </div>
                    </div>
                    <div className={styles.actions}>
                        {(ACL.check(UserCan.LABEL_THINGS, userCanDo) ||
                            (isSubAdmin && subUsers.includes(itemId))) && (
                                <>
                                    <div>
                                        <Link
                                            to="/extra-info"
                                            className={styles.actions__secondaryButton}
                                            title='New Label'
                                        >
                                            <span>New Label</span>
                                        </Link>
                                        {/* <button
                                                    onClick={this.handleAddCategory}
                                                    className={styles.actions__primaryButton}
                                                >
                                                    <AddIcon />
                                                    <span>Add Category</span>
                                                </button> */}

                                        <Link
                                            to={`/${isFence ? 'fence' : 'device'}/${itemId}/notes/add/${keyGen()}`}
                                            className={styles.actions__primaryButton}
                                        >
                                            <span>Add Note</span>
                                        </Link>

                                        <button
                                            onClick={this.openClearModal}
                                            className={styles.actions__clearAllButton}
                                        >
                                            <span>Clear All</span>
                                        </button>

                                        <button
                                            onClick={this.openCopyModal}
                                            className={styles.actions__copyDataButton}
                                        >
                                            <span>Copy to Clipboard</span>
                                        </button>
                                    </div>

                                    <ClearModal
                                        open={this.state.clearModalOpen}
                                        onClose={this.closeClearModal}
                                        onConfirm={this.clearTable}
                                        title="Delete All Unit Information"
                                        bodyText="Are you sure you want to delete all extra info for this unit? This action cannot be undone."
                                    />
                                </>
                            )}
                    </div>
                    {/* <div style={{ paddingBottom: needsSave ? 35 : 0 }}></div> */}
                </DynamicLayout>

                {newOverlay && (
                    <LabelSelectorOverlay
                        onSelected={(key) =>
                            this.setState(pipe(assocPath(['toSave', key], { text: '' }), assoc('newOverlay', false)))
                        }
                        possible={omit(keys(toSave), allLabels)}
                        close={this.stateSetter({ newOverlay: false })}
                        toSave={toSave}
                    />
                )}
                {newCategoryOverlay && (
                    <LabelSelectorOverlay
                        onSelected={(key) =>
                            this.setState(prevState => {
                                let existingValue: string | iExtraInfoItemValue | ICategory = prevState.toSave[key] || { text: '' };
                                let sourceCategory: string | null = null;

                                const isICategory = (data: string | iExtraInfoItemValue | ICategory): data is ICategory => {
                                    return typeof data === 'object' && 'isCategory' in data;
                                };

                                for (const [catName, catData] of Object.entries(prevState.toSave)) {
                                    if (isICategory(catData) && catData.isCategory && key in catData && key !== 'isCategory') {
                                        sourceCategory = catName;
                                        existingValue = catData[key] as string | iExtraInfoItemValue;
                                        break;
                                    }
                                }

                                let newState = assocPath(['toSave', newCategoryOverlay, key], existingValue, prevState);

                                if (sourceCategory) {
                                    newState = dissocPath(['toSave', sourceCategory, key], newState);
                                } else if (prevState.toSave[key] !== undefined) {
                                    newState = dissocPath(['toSave', key], newState);
                                }

                                return pipe(() => newState, assoc('newCategoryOverlay', null))();
                            }, this._saveChanges.bind(this))
                        }
                        possible={allLabels}
                        close={this.stateSetter({ newCategoryOverlay: null })}
                        toSave={toSave}
                        currentCategory={newCategoryOverlay}
                    />
                )}

                {this.state.showDatePicker && (
                    <Modal>
                        <div className={styles.dayPickerContainer}>
                            <MaskedInput
                                id="dateInput"
                                className={styles.maskedDateInput}
                                mask={dateMask}
                                guide={false}
                                value={
                                    this.state.selectedDate
                                        ? moment(this.state.selectedDate).format(
                                            CALENDAR_INPUT_FORMAT
                                        )
                                        : ''
                                }
                                onChange={(e) =>
                                    this.changeInputDate(e.target.value.trim())
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();

                                        this.setState({ showDatePicker: false });
                                        this.handleDateSelect(this.state.selectedDate);
                                    }
                                }}
                            />

                            <DayPicker
                                selectedDays={this.state.selectedDate}
                                month={this.state.displayMonth}
                                onMonthChange={(month) =>
                                    this.setState({ displayMonth: month })
                                }
                                onDayClick={this.handleDateSelect}
                                showOutsideDays
                                modifiers={{ today: new Date() }}
                                modifiersStyles={{
                                    today: {
                                        color: '#FF7C02',
                                        fontWeight: 'bold',
                                        backgroundColor: '#FFEFE6',
                                    },
                                    outside: {
                                        color: '#ccc',
                                    },
                                }}
                                navbarElement={(navbarProps) => (
                                    <CustomNavBar
                                        {...navbarProps}
                                        handleMonthChange={this.changeInputDate}
                                    />
                                )}
                                captionElement={() => null}
                            />

                            <button
                                onClick={() => {
                                    this.setState((prevState) => ({
                                        ...prevState,
                                        showDatePicker: false,
                                    }));
                                    this.handleDateSelect(this.state.selectedDate);
                                }}
                                className={styles.dayPickerClose}
                            >
                                <CloseIcon style={{ fill: '#878889' }} />
                            </button>

                            <div className={styles.dayPickerFooter}>
                                <button
                                    className={styles.cancelButton}
                                    onClick={() => {
                                        this.setState({ showDatePicker: false });
                                    }}
                                >
                                    Cancel
                                </button>

                                <button
                                    className={styles.okButton}
                                    onClick={() => {
                                        this.setState({ showDatePicker: false });
                                        this.handleDateSelect(this.state.selectedDate);
                                    }}
                                >
                                    Ok
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}

                <GalleryModal
                    open={!!this.state.galleryOpen && !!this.state.galleryIdx}
                    files={this.state.galleryIdx ? this.getFilesFor(this.state.galleryIdx, this.state.galleryCategory) : []}
                    initialIndex={this.state.gallerySlideIndex || 0}
                    onClose={this.closeGallery}
                    onDeleteAt={(index) => {
                        const idx = this.state.galleryIdx!;
                        const cat = this.state.galleryCategory || undefined;
                        this.removeFileAt(idx, cat, index);
                    }}
                    onIndexChange={(i) => this.setState({ gallerySlideIndex: i })}
                />

                {(() => {
                    const labelId = this.state.editingLabelId;
                    const labelData = labelId ? this.props.allLabels[labelId] : null;
                    if (!labelId || !labelData) return null;

                    return (
                        <NewTagOrLabelModal
                            onClose={this.closeLabelEditor}
                            handleSubmit={this.handleUpdateLabel}
                            isTagType={false}
                            initialValues={{
                                tagName: labelData.details.name,
                                kind: labelsDb.normalizeLabelKind((labelData.details as any).kind),
                            }}
                            onDelete={this.handleDeleteLabel}
                        />
                    );
                })()}

                <Dialog setupConfig={this.setupDialog} />
                {this.state.importModalOpen && (
                    <div className={classNames('modal-overlay', styles.overlayCss)} onClick={this.closeImportModal}>
                        <div className={styles.modal} onClick={event => event.stopPropagation()}>
                            <div className={styles.modalHeader}>
                                <span className={styles.modalTitle}>Import Extra Info via CSV or XLSX</span>
                                <div className={styles.buttonsSection}>
                                    <Tooltip title={'Close Modal'} placement='top'>
                                        <button onClick={this.closeImportModal} className={styles.closeModalButton}>
                                            <CloseIcon />
                                        </button>
                                    </Tooltip>
                                </div>
                            </div>
                            <div className={styles.importModalBody}>
                                <p>
                                    To import extra information into this unit, you can either upload your own CSV file or fill out
                                    and upload the sample provided{' '}
                                    <span className={styles.downloadCsvSampleLink} onClick={this.exportEmptyExtraInfoCsv}>
                                        here
                                    </span>.
                                </p>
                                <button
                                    onClick={this.triggerFileInput}
                                    className={styles.actions__primaryButton}
                                    style={{ marginTop: '10px' }}
                                >
                                    Upload File
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    componentDidUpdate(prevProps: IFullProps) {
        if (prevProps.match.params.itemId !== this.props.match.params.itemId) {
            const { item } = this.props;
            const oldExtraInfo = item['extra-info'];
            const migrated = migrateExtraInfo(oldExtraInfo);

            this.setState({
                toSave: migrated || {},
                newOverlay: false,
                newCategoryOverlay: null,
                currentItemIdx: undefined,
                currentCategory: null,
                editingId: null,
                editingValues: {},
                importModalOpen: false,
                clearModalOpen: false,
                copyModalOpen: false,
                galleryOpen: false,
                rowOrder: []
            }, () => {
                this.initializeRowOrder();
            });
        }

        if (!equals(prevProps.allLabels, this.props.allLabels)) {
             this.initializeRowOrder();
        }
    }

    openImportModal = () => this.setState({ importModalOpen: true });
    closeImportModal = () => this.setState({ importModalOpen: false });

    render() {
        return this.debugRender();
    }
}

const mapDispatchToProps = (dispatch: any) => ({
    dispatch: (action: any) => dispatch(action)
});
export default connect(mapStateToProps, mapDispatchToProps)(LabelItemComponent);

const LabelCell = ({ label }: { label: string }) => (
    <div className={styles.labelCell}>{label}</div>
);

const TextCell = ({
    idx,
    label,
    value,
    readOnly,
    onChangeHandler,
    type,
    activeIdx,
    updateActiveIdx,
    allDeviceIds,
    allPeopleIds,
    kind
}: {
    idx: string;
    label: string;
    value: string;
    readOnly?: boolean;
    onChangeHandler: (v: string) => void;
    type: ItemType;
    activeIdx: string | null | undefined;
    updateActiveIdx: (idx: string) => void;
    allDeviceIds: string[];
    allPeopleIds: string[];
    kind: string;
}) => {
    return (
        <FieldSuggestions
            idx={idx}
            readOnly={readOnly}
            onChange={pipe(targetVal, onChangeHandler)}
            onChangeHandler={onChangeHandler}
            value={value}
            type={type}
            activeIdx={activeIdx}
            updateActiveIdx={updateActiveIdx}
            allDeviceIds={allDeviceIds}
            allPeopleIds={allPeopleIds}
            kind={kind}
            hideSuggestions={label.toLowerCase() === 'vin' || label.toLowerCase() === 'plate' || label.toLowerCase() === 's/n'}
        />
    );
};

const DateCell = ({
    idx,
    value,
    isDateValid,
    onDateChange,
    onCalendarClick,
    onClearDate
}: {
    idx: string;
    value: string;
    isDateValid: boolean;
    onDateChange: (v: string) => void;
    onCalendarClick: () => void;
    onClearDate: () => void;
}) => (
    <div className={styles.dateCell}>
        {value ? (
            <>
                <input
                    type="text"
                    value={value}
                    onChange={e => onDateChange(e.target.value)}
                    onClick={onCalendarClick}
                    placeholder="MM-DD-YYYY"
                    className={styles.dateInput}
                    style={isDateValid
                        ? { cursor: 'pointer', textAlign: 'center' as React.CSSProperties['textAlign'] }
                        : { cursor: 'pointer', textAlign: 'center' as React.CSSProperties['textAlign'], border: '1px solid red', background: '#ffeaea' }}
                />
                <Tooltip title="Clear Date" placement="top">
                    <button onClick={onClearDate}><CloseIcon /></button>
                </Tooltip>
            </>
        ) : (
            <div className={styles.dateCell__actions} style={{ display: 'flex', flexDirection: 'row', gap: 4, justifyContent: 'center' }}>
                <Tooltip title="Add Date" placement="top">
                    <button onClick={onCalendarClick} className={styles.row__head__actions__button}>
                        <CalendarTodayIcon />
                    </button>
                </Tooltip>
            </div>
        )}
    </div>
);

const FileCell = ({
    files,
    onUploadClick,
    onRemoveAll,
    onOpenGallery,
    cameraBtnClass,
    removeBtnClass,
    isDragOver,
}: {
    files?: iExtraInfoFile[];
    onUploadClick: () => void;
    onRemoveAll: () => void;
    onOpenGallery: (startAt?: number) => void;
    cameraBtnClass?: string;
    removeBtnClass?: string;
    isDragOver?: boolean;
}) => {
    const hasFiles = Array.isArray(files) && files.length > 0;

    return (
        <div
            className={classNames(styles.fileCell, {
                [styles.dragOver]: isDragOver
            })}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
            {hasFiles && (
                <button
                    type="button"
                    style={{
                        padding: 0,
                        margin: 0,
                        marginRight: 6,
                        textDecoration: 'underline',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                    }}
                    onClick={() => onOpenGallery(0)}
                    title={files!.length === 1 ? 'Open file' : 'Open files'}
                >
                    {files!.length === 1 ? 'File' : 'Files'}
                </button>
            )}

            <Tooltip title="Upload image(s) or PDF(s)" placement="top">
                <button onClick={onUploadClick} className={cameraBtnClass || styles.row__head__actions__button}>
                    <PhotoCameraIcon />
                </button>
            </Tooltip>
            {hasFiles && (
                <Tooltip title="Remove all files" placement="top">
                    <button onClick={onRemoveAll} className={removeBtnClass || styles.row__head__actions__button}>
                        <CloseIcon />
                    </button>
                </Tooltip>
            )}
        </div>
    );
};

interface ExtraInfoRowProps {
    idx: string;
    label: string;
    textValue: string;
    dateValue: string;
    isDateValid: boolean;
    readOnly?: boolean;
    files?: iExtraInfoFile[];
    type: ItemType;
    activeIdx: string | null | undefined;
    updateActiveIdx: (idx: string) => void;
    allDeviceIds: string[];
    allPeopleIds: string[];
    onTextChange: (v: string) => void;
    onToggle?: (value: boolean) => void;
    onDateChange: (v: string) => void;
    onCalendarClick: () => void;
    onClearDate: () => void;
    onUploadClick: () => void;
    onFileDrop?: (file: File) => void;
    onRemoveAll: () => void;
    onOpenGallery: (startAt?: number) => void;
    actionsCell?: React.ReactNode;
    booleanValue?: boolean;
    isDragOver?: boolean;
    renderMode: string;
    onEditLabel?: () => void;
}

export function useOnClickOutside(ref: React.RefObject<HTMLElement>, handler: (event: MouseEvent) => void) {
    useEffect(() => {
        const listener = (event: MouseEvent) => {
            if (!ref.current || ref.current.contains(event.target as Node)) return;
            handler(event);
        };
        document.addEventListener('mousedown', listener);
        return () => { document.removeEventListener('mousedown', listener); };
    }, [ref, handler]);
}

const ExtraInfoRow: React.FC<ExtraInfoRowProps> = ({
    idx,
    label,
    textValue,
    dateValue,
    booleanValue,
    onToggle,
    isDateValid,
    readOnly,
    files,
    type,
    activeIdx,
    updateActiveIdx,
    allDeviceIds,
    allPeopleIds,
    onTextChange,
    onDateChange,
    onCalendarClick,
    onClearDate,
    onUploadClick,
    onFileDrop,
    onRemoveAll,
    onOpenGallery,
    actionsCell,
    isDragOver,
    renderMode
}) => {
    const rowRef = useRef<HTMLTableDataCellElement>(null);

    useOnClickOutside(rowRef, () => {
        if (activeIdx === idx) updateActiveIdx(null);
    });

    return (
        <>
            {actionsCell && (
                <td ref={rowRef} key={idx} className={classNames(styles.cell, styles.labelCell)}>
                    <LabelCell label={label} />
                </td>)}
            <td ref={rowRef} className={classNames(styles.cell, styles.textCell)} style={renderMode === 'boolean' || renderMode === 'legacy-boolean' ? { textAlign: 'center' } : {}}>
                {renderMode === 'boolean' || renderMode === 'legacy-boolean' ? (
                    <label className={styles.switcher}>
                        <StyledEngineProvider injectFirst>
                            <ThemeProvider theme={muiSwitchTheme}>
                                <Switch checked={!!booleanValue} onChange={(event) => onToggle?.(event.target.checked)} size="small" disabled={readOnly} />
                            </ThemeProvider>
                        </StyledEngineProvider>
                    </label>
                ) : (
                    <TextCell idx={idx} label={label} value={textValue} readOnly={readOnly} onChangeHandler={onTextChange} type={type} activeIdx={activeIdx} updateActiveIdx={updateActiveIdx} allDeviceIds={allDeviceIds} allPeopleIds={allPeopleIds} kind={renderMode} />
                )}
            </td>
            <td className={classNames(styles.cell)}>
                <DateCell
                    idx={idx}
                    value={dateValue}
                    isDateValid={isDateValid}
                    onDateChange={onDateChange}
                    onCalendarClick={onCalendarClick}
                    onClearDate={onClearDate}
                />
            </td>
            <td ref={rowRef} className={classNames(styles.cell, styles.imageCell)}>
                <FileCell
                    files={files}
                    onUploadClick={onUploadClick}
                    onRemoveAll={onRemoveAll}
                    onOpenGallery={onOpenGallery}
                    isDragOver={isDragOver}
                />
            </td>
            {actionsCell && (
                <td ref={rowRef} className={classNames(styles.cell, styles.actionsCell)}>
                    {actionsCell}
                </td>
            )}
        </>
    );
};

function normalizeToNewExtraInfoFormat(value, type: 'text' | 'date' | 'image' | 'pdf', extra: any = {}) {
    if (type === 'text') return { text: value };
    if (type === 'date') return { date: value };
    if (type === 'image') return { files: [{ url: value, format: 'image' }] };
    if (type === 'pdf') return { files: [{ url: value, fileName: extra.fileName || 'Document.pdf', format: 'pdf' }] };
    return {};
}

export function migrateExtraInfo(oldData: any): any {
    if (!oldData || typeof oldData !== 'object') {
        const result = typeof oldData === 'string' ? { text: oldData } : oldData;
        return result;
    }

    if ((oldData as any).isCategory) {
        const migratedCategory: any = { isCategory: true };
        let hasAny = false;
        for (const [key, value] of Object.entries(oldData)) {
            if (key === 'isCategory') continue;
            const migrated = migrateExtraInfo(value);
            if (migrated !== undefined) {
                migratedCategory[key] = migrated;
                hasAny = true;
            }
        }
        const result = hasAny ? migratedCategory : undefined;
        return result;
    }

    if (typeof oldData === 'string') {
        const result = { text: oldData };
        return result;
    }

    if (typeof oldData === 'object' && oldData !== null && !Array.isArray(oldData)) {
        const keys = Object.keys(oldData);
        const isElement = keys.some(k =>
            ['isDate', 'isPdf', 'isPhoto', 'text', 'url', 'fileName', 'file', 'files', 'date'].includes(k)
        );

        if (isElement) {
            const onlyDate =
                'date' in (oldData as any) &&
                Object.keys(oldData).filter(k =>
                    !['date', 'isDate'].includes(k) &&
                    (oldData as any)[k] !== undefined &&
                    (oldData as any)[k] !== null &&
                    (oldData as any)[k] !== ''
                ).length === 0;

            if (onlyDate) {
                let dateValue = (oldData as any).date;
                if (typeof dateValue === 'object' && dateValue !== null && typeof (dateValue as any).format === 'function') {
                    dateValue = (dateValue as any).format('MM-DD-YYYY');
                } else {
                    dateValue = typeof dateValue === 'string' ? dateValue : String(dateValue);
                }
                return { date: dateValue };
            }

            const result: any = {};

            if ((oldData as any).text) result.text = (oldData as any).text;

            if ((oldData as any).date) {
                const d = (oldData as any).date;
                if (typeof d === 'object' && d !== null && typeof d.format === 'function') {
                    result.date = d.format('MM-DD-YYYY');
                } else {
                    result.date = typeof d === 'string' ? d : String(d);
                }
            }

            if ((oldData as any).url || (oldData as any).file || (oldData as any).files) {
                const buildFile = (f: any) => {
                    if (!f) return undefined;
                    return {
                        url: f.url,
                        fileName: f.fileName ?? 'Document.pdf',
                        format: f.format ?? (f.isPdf ? 'pdf' : f.isPhoto ? 'image' : undefined),
                    } as iExtraInfoFile;
                };

                if ((oldData as any).files && Array.isArray((oldData as any).files)) {
                    result.files = (oldData as any).files.map(buildFile).filter(Boolean);
                } else if ((oldData as any).file || (oldData as any).url) {
                    const singleFile = buildFile({
                        url: (oldData as any).url || (oldData as any).file?.url,
                        fileName: (oldData as any).fileName || (oldData as any).file?.fileName,
                        format: (oldData as any).file?.format,
                        isPdf: (oldData as any).isPdf,
                        isPhoto: (oldData as any).isPhoto,
                    });
                    result.files = singleFile ? [singleFile] : [];
                }
            }

            return Object.keys(result).length ? result : undefined;
        } else {
            let hasAny = false;
            const migrated: any = {};
            for (const [key, value] of Object.entries(oldData)) {
                if (typeof value === 'string') {
                    migrated[key] = { text: value };
                    hasAny = true;
                } else {
                    const migratedValue = migrateExtraInfo(value);
                    if (migratedValue !== undefined) {
                        migrated[key] = migratedValue;
                        hasAny = true;
                    }
                }
            }
            return hasAny ? migrated : undefined;
        }
    }

    return oldData;
}